// Gap in pixels between the tops of stacked cards
const STACK_GAP = 130;

/**
 * Returns the currently applied translateY value (px) of an element.
 *
 * @param {HTMLElement} el - The element to read.
 */
const readTranslateY = (el) => {
  const m = el.style.transform.match(/translateY\(([^)]+)\)/);
  const v = m ? parseFloat(m[1]) : 0;
  return Number.isNaN(v) ? 0 : v;
};

class FieldCards {
  // eslint-disable-next-line class-methods-use-this
  setCards() {
    Drupal.behaviors.fieldCards = {
      attach(context) {
        const wrappers = once(
          'field-cards',
          '.paragraph--type--cards .field--name-field-cards',
          context
        );

        wrappers.forEach((wrapper) => {
          const items = Array.from(
            wrapper.querySelectorAll(
              ':scope > .field__items > .field__item, :scope > .field__item'
            )
          );

          if (items.length < 2) return;

          // Later cards stack above earlier ones; card 0 gets the lowest base z-index
          items.forEach((item, index) => {
            item.style.zIndex = 10 + index;
            item.style.position = 'relative';
          });

          // Record the natural top offset of every item relative to the
          // wrapper BEFORE any transforms are applied. Used to compute the
          // correct visual height for the wrapper while cards are stacked.
          const wrapperTop0 = wrapper.getBoundingClientRect().top;
          const naturalTops = items.map(
            (item) => item.getBoundingClientRect().top - wrapperTop0
          );

          // Allow the wrapper height to animate smoothly.
          wrapper.style.overflow = 'hidden';
          wrapper.style.transition = 'height 0.4s ease';
          wrapper.style.marginBottom = '40px';

          // Indices of cards that have been animated into the stacked position
          const stackedCards = new Set();
          // Index of the currently expanded card (null = none)
          let expandedIndex = null;
          // Stores the computed translateY for each stacked card index so
          // non-stacked followers can mirror the same shift.
          const stackedTranslates = {};

          /**
           * Returns the translateY pixel value needed to pull card `index` into
           * its stacked position below card 0.
           *
           * @param {number} index - Index of the card.
           */
          const getStackedTranslate = (index) => {
            // Use pre-recorded natural offsets so the result is stable
            // regardless of ongoing CSS transitions or scroll position.
            // naturalTops[n] is the item's top relative to the wrapper
            // before any transforms were applied.
            return -(naturalTops[index] - naturalTops[0] - index * STACK_GAP);
          };

          /**
           * Recalculate and apply transforms for every stacked card.
           * Non-stacked cards that follow the highest stacked card are shifted
           * up by the same cumulative amount as the last stacked card, keeping
           * the original whitespace between them intact.
           *
           * Two-pass approach: first compute all stacked translates, then apply
           * them (and the follow-along offset) so the follower logic always reads
           * a fresh value even for cards stacked in the same call.
           */
          const applyPositions = () => {
            // ── Pass 1: compute and store every stacked card's translateY ──
            stackedCards.forEach((index) => {
              if (expandedIndex !== null && index >= expandedIndex) {
                stackedTranslates[index] = 0;
              } else {
                stackedTranslates[index] = getStackedTranslate(index);
              }
            });

            // The follow-along offset equals the stored translateY of the
            // highest-indexed stacked card (i.e. how far up that card moved).
            let followTranslate = 0;
            if (stackedCards.size > 0) {
              const highestStacked = Math.max(...stackedCards);
              followTranslate = stackedTranslates[highestStacked] || 0;
            }

            //Pass 2: apply transforms to all cards
            const appliedTranslates = {};
            items.forEach((item, index) => {
              if (!stackedCards.has(index)) {
                if (index > 0 && followTranslate !== 0) {
                  // Non-stacked card that should follow the stacked cards above
                  // it: shift by the same amount so the gap is preserved.
                  item.style.transition =
                    'transform 0.4s ease, clip-path 0.4s ease';
                  item.style.transform = `translateY(${followTranslate}px)`;
                  appliedTranslates[index] = followTranslate;
                } else {
                  item.style.transform = '';
                  item.style.transition = '';
                  appliedTranslates[index] = 0;
                }
                return;
              }

              // Expanded card AND all cards after it
              // Return to natural DOM position so all CSS margins / padding /
              // whitespace that existed before the accordion animation are
              // fully restored — both above and below each card.
              if (expandedIndex !== null && index >= expandedIndex) {
                item.style.transition =
                  'transform 0.4s ease, clip-path 0.4s ease';
                item.style.transform = 'translateY(0)';
                appliedTranslates[index] = 0;
                return;
              }

              // Normal stacked position (pulled up near card 0)
              const translate = stackedTranslates[index];
              item.style.transition =
                'transform 0.4s ease, clip-path 0.4s ease';
              item.style.transform = `translateY(${translate}px)`;
              appliedTranslates[index] = translate;
            });

            // Crop overlapping cards bottom-up
            if (stackedCards.size === 0) {
              items.forEach((item) => {
                item.style.clipPath = '';
              });
            } else {
              let allowedBottom = Infinity;
              for (let index = items.length - 1; index >= 0; index -= 1) {
                const item = items[index];
                const visualTop = naturalTops[index] + appliedTranslates[index];
                const visualBottom = visualTop + item.offsetHeight;
                const effectiveBottom = Math.min(visualBottom, allowedBottom);
                const crop = Math.max(0, visualBottom - effectiveBottom);
                item.style.clipPath =
                  crop > 0 ? `inset(0 0 ${crop}px 0)` : 'none';
                allowedBottom = effectiveBottom;
              }
            }

            // Shrink / grow the wrapper to match the visual card area
            if (stackedCards.size === 0) {
              wrapper.style.height = '';
            } else {
              const lastIdx = items.length - 1;
              const lastItem = items[lastIdx];
              const lastTranslate = readTranslateY(lastItem);
              const visibleBottom =
                naturalTops[lastIdx] + lastTranslate + lastItem.offsetHeight;
              wrapper.style.height = `${visibleBottom}px`;
            }
          };

          /**
           * Checks whether each un-stacked card has crossed the trigger line and
           * starts its stacking animation when it does.
           *
           * Trigger: card.top < 50 px below the top of the viewport.
           * Only the triggering card is added to stackedCards; cards after it
           * follow along (preserving whitespace) until they reach the threshold.
           */
          const checkScroll = () => {
            // Collect all cards that crossed the threshold first, then apply
            // positions once.  Calling applyPositions() inside the loop would
            // start CSS transitions mid-iteration; subsequent cards would read
            // partially-animated getBoundingClientRect values and compute wrong
            // translates when several cards need to stack in the same frame.
            let changed = false;
            items.forEach((item, index) => {
              if (index === 0) return;
              if (stackedCards.has(index)) return;

              const rect = item.getBoundingClientRect();

              if (rect.top < 950) {
                stackedCards.add(index);
                changed = true;
              }
            });
            if (changed) applyPositions();
          };

          /**
           * Expands card at `index` so its full content is visible and pushes
           * all following cards down into closed overlapping state.
           *
           * @param {number} index - Index of the card to expand.
           */
          const expandCard = (index) => {
            if (expandedIndex === index) return;
            expandedIndex = index;
            // Give the expanded card the highest z-index
            items.forEach((item, i) => {
              item.style.zIndex = i === index ? 10 + items.length : 10 + i;
            });
            applyPositions();
          };

          /**
           * Collapses the currently expanded card back into the stack.
           */
          const collapseCard = () => {
            if (expandedIndex === null) return;
            expandedIndex = null;
            // Restore default z-indices
            items.forEach((item, i) => {
              item.style.zIndex = 10 + i;
            });
            applyPositions();
          };

          const lastIndex = items.length - 1;

          // Attach click/focus listeners to every card except the last
          // (the last card is always fully visible on top of the stack)
          items.forEach((item, index) => {
            if (index === lastIndex) return;

            item.addEventListener('click', () => {
              // Card 0 may not be in stackedCards yet but can still be expanded
              if (!stackedCards.has(index) && index !== 0) return;
              if (expandedIndex === index) {
                collapseCard();
              } else {
                expandCard(index);
              }
            });

            item.addEventListener('focusin', () => {
              if (stackedCards.has(index) || index === 0) expandCard(index);
            });

            item.addEventListener('focusout', (e) => {
              if (!item.contains(e.relatedTarget)) collapseCard();
            });
          });

          // Tracks whether the scroll-stacking animation is active.
          let scrollAnimationEnabled = window.innerWidth >= 1200;

          // Resize listener: reset animation state so it can replay after resize.
          let resizeThrottle = null;
          window.addEventListener('resize', () => {
            if (resizeThrottle) return;
            resizeThrottle = setTimeout(() => {
              resizeThrottle = null;
              // Reset all state
              expandedIndex = null;
              stackedCards.clear();
              Object.keys(stackedTranslates).forEach(
                (k) => delete stackedTranslates[k]
              );
              // Remove all inline transforms / transitions / crops
              items.forEach((item, i) => {
                item.style.transform = '';
                item.style.transition = '';
                item.style.clipPath = '';
                item.style.zIndex = 10 + i;
              });
              wrapper.style.height = '';
              if (window.innerWidth < 1200) {
                // Below breakpoint: keep wrappers reset and disable scroll animation
                scrollAnimationEnabled = false;
                return;
              }
              scrollAnimationEnabled = true;
              // Re-record natural tops after layout has settled
              const newWrapperTop = wrapper.getBoundingClientRect().top;
              items.forEach((item, i) => {
                naturalTops[i] =
                  item.getBoundingClientRect().top - newWrapperTop;
              });
              // Re-evaluate the current scroll position
              checkScroll();
            }, 1000);
          });

          // While a card is expanded, ignore all scroll effects entirely.
          // The user must click/tab the card again to close it.
          let scrollThrottle = null;
          window.addEventListener('scroll', () => {
            if (!scrollAnimationEnabled) return;
            if (expandedIndex !== null) return;

            if (scrollThrottle) return;
            scrollThrottle = setTimeout(() => {
              checkScroll();
              scrollThrottle = null;
            }, 100);
          });

          // Initial check in case the section is already in view on load
          if (scrollAnimationEnabled) checkScroll();
        });
      }
    };
  }

  run() {
    this.setCards();
  }
}

export default FieldCards;
